import { FileText, LayoutDashboard, LogOut, Settings } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { PreferenceControls } from "@/components/app-settings/preference-controls";
import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";
import { cn } from "@/lib/utils/cn";

const navItems = [
  { to: workbenchRoutes.dashboard, labelKey: "nav.dashboard", icon: LayoutDashboard },
  { to: workbenchRoutes.quotations, labelKey: "nav.quotations", icon: FileText },
  { to: workbenchRoutes.settings, labelKey: "nav.settings", icon: Settings, adminOnly: true },
] as const;

export function AdminLayout() {
  const { adminLogoFileId, headerSubtitle, headerTitle, t } = useAppSettings();
  const { user, logout } = useAuth();
  const location = useLocation();
  const isQuoteEditor = location.pathname.startsWith(`${workbenchRoutes.quotations}/`) && location.pathname.endsWith("/edit");

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-border bg-background px-3 py-4 md:block">
        <div className="px-2 pb-5">
          <div className="flex items-center gap-2">
            {adminLogoFileId ? <img src={`/api/brand-assets/${adminLogoFileId}`} alt="" className="h-7 w-7 rounded object-contain" /> : null}
            <div>
              <div className="text-base font-semibold tracking-normal">{headerTitle || t("app.name")}</div>
              <div className="text-xs text-muted-foreground">{headerSubtitle || t("app.subtitle")}</div>
            </div>
          </div>
        </div>
        <nav className="space-y-1">
          {navItems.filter((item) => !("adminOnly" in item && item.adminOnly) || user?.role === "admin").map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex h-9 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition hover:bg-muted hover:text-foreground",
                    isActive && "bg-muted text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t(item.labelKey)}
              </NavLink>
            );
          })}
        </nav>
      </aside>
      <main className="md:pl-60">
        <div className={cn("mx-auto px-4 py-5 md:px-6", isQuoteEditor ? "max-w-none 2xl:px-8" : "max-w-7xl")}>
          <div className="mb-5 flex flex-wrap items-center justify-end gap-3">
            {user ? (
              <div className="text-right text-xs text-muted-foreground">
                <div className="font-medium text-foreground">{user.name}</div>
                <div>{user.jobTitle} · {user.email}</div>
              </div>
            ) : null}
            <PreferenceControls />
            <Button type="button" variant="secondary" className="h-8 px-2" onClick={() => void logout()} title={t("auth.logout")}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {t("auth.logout")}
            </Button>
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
